/**
 * Utilitário para exportar dados para Excel
 */
import * as XLSX from 'xlsx'

export interface ExportColumn {
  key: string
  label: string
  format?: (value: any) => string | number
}

export interface ExportOptions {
  filename?: string
  sheetName?: string
}

/**
 * Exporta dados para Excel
 * @param data Array de objetos com os dados
 * @param columns Definição das colunas (chave e label)
 * @param options Opções adicionais (filename, sheetName)
 */
export function exportToExcel<T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn[],
  options: ExportOptions = {}
): void {
  if (!data || data.length === 0) {
    alert('Não há dados para exportar')
    return
  }

  // Preparar dados para Excel
  const excelData = data.map((row) => {
    const excelRow: Record<string, any> = {}
    columns.forEach((col) => {
      const value = row[col.key]
      excelRow[col.label] = col.format ? col.format(value) : value ?? ''
    })
    return excelRow
  })

  // Criar workbook
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(excelData)

  // Ajustar largura das colunas
  const colWidths = columns.map((col) => ({
    wch: Math.max(col.label.length, 15),
  }))
  ws['!cols'] = colWidths

  // Adicionar worksheet ao workbook
  XLSX.utils.book_append_sheet(wb, ws, options.sheetName || 'Dados')

  // Gerar nome do arquivo
  const filename = options.filename || `export_${new Date().toISOString().split('T')[0]}.xlsx`

  // Download
  XLSX.writeFile(wb, filename)
}

/**
 * Exporta dados de uma tabela HTML para Excel
 * @param tableId ID da tabela HTML
 * @param filename Nome do arquivo
 */
export function exportTableToExcel(tableId: string, filename?: string): void {
  const table = document.getElementById(tableId) as HTMLTableElement
  if (!table) {
    alert('Tabela não encontrada')
    return
  }

  const wb = XLSX.utils.table_to_book(table)
  const file = filename || `export_${new Date().toISOString().split('T')[0]}.xlsx`
  XLSX.writeFile(wb, file)
}
